import { POPULARITY_METADATA_BY_KEY, SpateMetricMetadata } from 'utils/metrics';
import {
    getSortOptionKey,
    useSortSearchParamsStateOnly,
} from 'utils/hooks/useSort';
import { useEffect, useMemo } from 'react';
import { healthStatFilterOptions } from 'utils/filters';
import { useFetchPopIndexAffinityRoots } from 'utils/hooks/api';
import { useFilterSearchParamsStateOnly } from 'utils/hooks/useFilter';
import useHandleSearchInputChange from 'utils/hooks/useHandleSearchInputChange';
import { useLocation } from 'react-router';
import usePopularityRelatedEntities from 'utils/hooks/usePopularityRelatedEntities';
import useTableColumns from 'utils/hooks/useTableColumns';
import useURLSearchParams from 'utils/hooks/useURLSearchParams';
import useStaleFilterNotice from 'utils/hooks/useStaleFilterNotice';

const metrics = [
    [POPULARITY_METADATA_BY_KEY.popularity_index_avg_12_month, true],
    [POPULARITY_METADATA_BY_KEY.YoY, true],
    [POPULARITY_METADATA_BY_KEY.increase_YoY, true],
    [POPULARITY_METADATA_BY_KEY.QoQ, false],
    [POPULARITY_METADATA_BY_KEY.increase_QoQ, false],
    [POPULARITY_METADATA_BY_KEY.MoM, false],
    [POPULARITY_METADATA_BY_KEY.increase_MoM, false],
    [POPULARITY_METADATA_BY_KEY.google_share_prc, false],
    [POPULARITY_METADATA_BY_KEY.tiktok_share_prc, false],
    [POPULARITY_METADATA_BY_KEY.instagram_share_prc, false],
].map(([config, visible]) => {
    const { healthStatKey: _healthStatKey, ...restConfig } = config.config;
    if (config.key === POPULARITY_METADATA_BY_KEY.YoY.key) {
        restConfig.healthStatKey = 'growth';
        restConfig.activeHealthStatLabel = true;
    }
    return [new SpateMetricMetadata(restConfig), visible];
});

function useRelatedEntitiesTableProps({
    affinityGroups,
    brands,
    categories,
    countries,
    getColumnsConfig,
    handleFilter,
    initialSortOption,
    initialFilterOption,
    name,
    paginateBy,
    filterParamSeparator = ',',
    filterParamPrefix,
    sortParamKey = 'sort',
}) {
    const searchParams = useURLSearchParams();
    const initialFilterKey = useMemo(
        () => JSON.stringify(initialFilterOption || {}),
        [initialFilterOption],
    );
    const stableInitialFilterOption = useMemo(
        () => initialFilterOption,
        [initialFilterKey],
    );
    const supportedFiltersKey = Object.keys(handleFilter || {})
        .sort()
        .join(',');
    const supportedFilters = useMemo(
        () => (supportedFiltersKey ? supportedFiltersKey.split(',') : []),
        [supportedFiltersKey],
    );
    const filterParamsSignature = useMemo(
        () => searchParams.toString(),
        [searchParams],
    );
    const popIndexGroupQuery = useFetchPopIndexAffinityRoots({
        categories: categories || null,
        brands: brands || null,
        countries: countries || null,
    });
    const fallbackGroupOptions = useMemo(() => {
        const paramKey = filterParamPrefix
            ? `${filterParamPrefix}popIndexParentName`
            : 'popIndexParentName';
        const paramValue = searchParams.get(paramKey);
        return paramValue
            ? paramValue
                  .split(filterParamSeparator)
                  .map((value) => value.trim())
                  .filter(Boolean)
                  .map((value) => ({ label: value, value }))
            : [];
    }, [filterParamPrefix, filterParamSeparator, searchParams]);
    const initialGroupOptions = useMemo(() => {
        const initialGroups =
            stableInitialFilterOption?.popIndexParentName || [];
        return initialGroups.map((option) => ({
            label: option.label ?? option.value,
            value: option.value ?? option.label,
        }));
    }, [stableInitialFilterOption]);
    const popIndexGroupOptions = useMemo(() => {
        return (popIndexGroupQuery.data || []).map(({ name, id, volume }) => ({
            label: name,
            value: id,
            volume,
        }));
    }, [popIndexGroupQuery.data]);
    const mergedGroupOptions = useMemo(() => {
        const optionsByValue = new Map(
            popIndexGroupOptions.map((option) => [option.value, option]),
        );
        initialGroupOptions.forEach((option) => {
            if (!optionsByValue.has(option.value)) {
                optionsByValue.set(option.value, option);
            }
        });
        fallbackGroupOptions.forEach((option) => {
            if (!optionsByValue.has(option.value)) {
                optionsByValue.set(option.value, option);
            }
        });
        return optionsByValue.size
            ? Array.from(optionsByValue.values())
            : initialGroupOptions.length
              ? initialGroupOptions
              : fallbackGroupOptions;
    }, [fallbackGroupOptions, initialGroupOptions, popIndexGroupOptions]);
    const healthStatOptions = useMemo(
        () => healthStatFilterOptions.map(({ options }) => options).flat(),
        [],
    );
    const filterAccessorsAndOptions = useMemo(
        () => ({
            healthStats: {
                accessor: (option) => option.value,
                options: healthStatOptions,
            },
            popIndexParentName: {
                accessor: (option) => option.value,
                options: mergedGroupOptions,
            },
        }),
        [healthStatOptions, mergedGroupOptions],
    );
    const urlFilterState = useFilterSearchParamsStateOnly({
        filterAccessorsAndOptions,
        initialFilterOption: stableInitialFilterOption,
        supportedFilters: Object.keys(handleFilter || {}),
        separator: filterParamSeparator,
        paramPrefix: filterParamPrefix,
    });
    const { filters, filtersKey, setFilter, setFilters, resetFilters } =
        urlFilterState;
    useStaleFilterNotice(urlFilterState.staleFilters);
    const nameFilter =
        filters && filters.name && filters.name.length ? filters.name[0] : null;

    const handleSearchInputChange = useHandleSearchInputChange({
        nameFilter,
        setFilter,
    });
    const initialSortKey = useMemo(
        () => JSON.stringify(initialSortOption || {}),
        [initialSortOption],
    );
    const stableInitialSortOption = useMemo(
        () => initialSortOption,
        [initialSortKey],
    );
    const sortOptions = useMemo(
        () =>
            metrics.flatMap(([metric]) => [
                metric.getSortOption({ ascending: true }),
                metric.getSortOption({ ascending: false }),
            ]),
        [],
    );
    const {
        setSortOption: baseSetSortOption,
        sortOption,
        sortOptionKey,
    } = useSortSearchParamsStateOnly({
        initialSortOption: stableInitialSortOption,
        options: sortOptions,
        sortOptionKeyAccessor: getSortOptionKey,
        paramKey: sortParamKey,
    });
    const queryData = {
        paginateBy,
        categories: categories || null,
        brands: brands || null,
        countries: countries || null,
        enabled: !!((categories || brands) && countries),
    };
    if (affinityGroups) queryData.affinity_group = affinityGroups;
    if (affinityGroups && filters?.popIndexParentName?.length) {
        throw new Error(
            'Cannot use both affinityGroups and popIndexParentName filters',
        );
    }
    const queryDataFilterAndSortConfig = {
        order_by: sortOption?.sortBy,
        ascending: sortOption?.ascending,
        ...(filters?.popIndexParentName?.length && {
            affinity_group: filters.popIndexParentName.map(
                ({ value }) => value,
            ),
        }),
        ...(filters?.name?.length && { search: filters.name[0] }),
        ...(filters?.healthStats?.filter(
            ({ metricLabel }) => metricLabel === 'price point',
        )?.length && {
            price_segmentation: filters?.healthStats
                ?.filter(
                    ({ metricLabel, label }) =>
                        metricLabel === 'price point' &&
                        ['High', 'Medium', 'Low'].includes(label),
                )
                .map(({ label }) => {
                    if (label === 'High') return 'HIGH PRICE';
                    if (label === 'Medium') return 'MEDIUM PRICE';
                    if (label === 'Low') return 'LOW PRICE';
                    return null;
                }),
        }),
    };
    const { query, getQueryData } = usePopularityRelatedEntities({
        ...queryDataFilterAndSortConfig,
        ...queryData,
    });
    const {
        fetchNextPage,
        isFetchingNextPage,
        fetchPreviousPage,
        hasNextPage,
        hasPreviousPage,
        isFetching,
        ...result
    } = query;
    const rows = (result.data?.pages || []).reduce((acc, { data }) => {
        if (!data) return acc;
        acc.push(...data.map((o) => ({ ...o, relatedName: name })));
        return acc;
    }, []);
    const isSuccess = result.isSuccess;
    const isLoading = result.isLoading || result.isFetchingNextPage;
    const dataReady = isSuccess && rows.length > 0;

    const { visibleOrderedMetrics, columnsMenu, setSortOption } =
        useTableColumns({
            tableName: `related-entities-${paginateBy}`,
            metrics,
            setSortOption: baseSetSortOption,
            sortOption,
        });

    const visibleColumnsConfig = getColumnsConfig(visibleOrderedMetrics);

    const isNoData = !(rows && rows.length);
    let noDataMessage;

    if (!isNoData && isSuccess) {
        noDataMessage = `No data to display. ${
            filters && Object.keys(filters).length
                ? 'Try adjusting the filters'
                : ''
        }`;
    } else if (isNoData && isLoading) {
        noDataMessage = 'Loading...';
    } else if (isNoData && result.isError) {
        noDataMessage =
            'Could not load the data. Refresh the page to try again.';
    } else if (isNoData) {
        noDataMessage = `No ${paginateBy} for this category.`;
    }

    const { pathname } = useLocation();
    useEffect(() => {
        const hasFilterParams = supportedFilters.some((key) =>
            searchParams.get(
                filterParamPrefix ? `${filterParamPrefix}${key}` : key,
            ),
        );
        const hasInitialFilters =
            Object.keys(stableInitialFilterOption || {}).length > 0;
        if (!hasFilterParams && hasInitialFilters) {
            resetFilters();
        }
    }, [
        pathname,
        resetFilters,
        filterParamsSignature,
        supportedFiltersKey,
        stableInitialFilterOption,
        filterParamPrefix,
    ]);

    return {
        columnsMenu,
        dataReady,
        isLoading,
        noDataMessage,
        rows,
        sortedData: rows,
        filters,
        filtersKey,
        setFilter,
        setFilters,
        resetFilters,
        sortOption,
        setSortOption,
        sortOptionKey,
        visibleColumnsConfig,
        handleSearchInputChange,
        queryData,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        pages: result.data?.pages,
        getQueryData,
    };
}

export default useRelatedEntitiesTableProps;
